// Facilities with Mailing Address — stand-in for the Tableau sheet of the
// same name. The cache stores Salesforce Billing* (one postal address per
// facility), not VitalEngine's separate mailing vs physical columns.
// A state filter and the usual customer / facility scope stand in for
// Tableau's Mailing State checklist.

import React, { useEffect, useMemo, useState } from 'react';
import { useCache } from '../../hooks/useCache';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useScopeSync } from '../../hooks/useScopeSync';
import { useCurrentScope } from '../../hooks/useCurrentScope';
import { useScope } from '../../contexts/ScopeContext';
import ScopeBar from '../../components/ScopeBar';
import FacilityFilter from '../../components/FacilityFilter';
import GroupFacilitiesModal from '../../components/GroupFacilitiesModal';
import { canonicalVeId } from '../../utils/drilldownAggregate';
import { downloadObjectsAsXlsx } from '../../utils/exportTableauSheet';
import './Drilldown20.css';
import './FacilitiesMailing.css';

function pick(row, ...keys) {
  for (const k of keys) {
    const v = row?.[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

function veIdOf(row) {
  return canonicalVeId(row?.vitalEngine_Facility_ID__c ?? row?.vitalEngineFacilityId);
}

// Salesforce stores the same state as "AL", "Alabama", or "alabama".
const STATE_BY_CODE = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  PR: 'Puerto Rico', GU: 'Guam', VI: 'U.S. Virgin Islands',
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
  ACT: 'Australian Capital Territory', NSW: 'New South Wales', QLD: 'Queensland',
  SA: 'South Australia', TAS: 'Tasmania', VIC: 'Victoria', WA_AU: 'Western Australia',
};

const STATE_BY_NAME = new Map(
  Object.entries(STATE_BY_CODE).map(([code, name]) => [name.toLowerCase(), name])
);
// Western Australia shares the abbreviation WA with Washington. Full name only.
delete STATE_BY_CODE.WA_AU;

function canonicalState(raw) {
  const s = String(raw || '').trim().replace(/\./g, '');
  if (!s) return '';
  const byCode = STATE_BY_CODE[s.toUpperCase()];
  if (byCode) return byCode;
  const byName = STATE_BY_NAME.get(s.toLowerCase());
  if (byName) return byName;
  return s;
}

function FacilitiesMailing() {
  const cache = useCache();
  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();
  const { clearScope: clearGlobalScope } = useScope();

  const [facilities, setFacilities] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [query, setQuery] = useState('');

  const [selectedFacilityIds, setSelectedFacilityIds] = useState(() => new Set());
  const [activeCustomerId, setActiveCustomerId] = useState('');
  const [activeFilterId, setActiveFilterId] = useState('');
  const [savedFilters, setSavedFilters] = useState([]);
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await cache.getCachedFacilities();
      setFacilities(r?.success ? (r.facilities || []) : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load facilities.');
    } finally {
      setLoading(false);
    }
  }, [cache]);

  useEffect(() => {
    load();
    (async () => {
      try {
        const r = await cache.getCachedFacilityHierarchy();
        if (r?.success) setHierarchy(r.hierarchy || []);
      } catch { /* optional */ }
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* none yet */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData: load,
    ready: true,
  });

  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) setSelectedFacilityIds(new Set());
    else {
      const sf = savedFilters.find(s => String(s.id) === String(filterId));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds.map(String)));
    }
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) setSelectedFacilityIds(new Set());
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids.map(String)));
    }
    setScopeFromCustomer(customerId);
  };

  const rows = useMemo(() => {
    return facilities
      .map(f => ({
        id: f.id || f.salesforceId,
        veId: veIdOf(f),
        name: pick(f, 'name', 'Name'),
        street: pick(f, 'billingStreet', 'BillingStreet'),
        city: pick(f, 'billingCity', 'BillingCity'),
        stateRaw: pick(f, 'billingState', 'BillingState'),
        state: canonicalState(pick(f, 'billingState', 'BillingState')),
        postal: pick(f, 'billingPostalCode', 'BillingPostalCode'),
      }))
      .filter(r => r.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [facilities]);

  const states = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.state) set.add(r.state);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const scope = selectedFacilityIds.size > 0
      ? new Set([...selectedFacilityIds].map(canonicalVeId))
      : null;
    return rows.filter(r => {
      if (scope && (!r.veId || !scope.has(r.veId))) return false;
      if (stateFilter && r.state.toLowerCase() !== stateFilter.toLowerCase()) return false;
      if (!q) return true;
      return [r.name, r.street, r.city, r.state, r.stateRaw, r.postal].some(v => v.toLowerCase().includes(q));
    });
  }, [rows, query, stateFilter, selectedFacilityIds]);

  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    for (const r of rows) if (r.veId) ids.add(r.veId);
    return ids;
  }, [rows]);

  return (
    <div className="dd20 fam">
      <p className="dd20-intro">
        Facility directory with the address stored on the Salesforce facility
        record, in the spirit of the Tableau sheet
        <em> Facilities with Mailing Address</em>.
        Pick a state, or scope a customer, to list the facilities in an area.
      </p>

      <div className="dd20-controls">
        <div className="dd20-field">
          <label htmlFor="fam-state">State</label>
          <select id="fam-state" value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
            <option value="">All states</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="dd20-field">
          <label htmlFor="fam-search">Find</label>
          <input
            id="fam-search"
            type="search"
            placeholder="Name, city, street…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        {savedFilters.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="fam-saved">Saved filter</label>
            <select id="fam-saved" value={activeFilterId} onChange={e => applySavedFilter(e.target.value)}>
              <option value="">None (show all)</option>
              {savedFilters.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.facilityIds?.length || 0})</option>
              ))}
            </select>
          </div>
        )}

        {customers.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="fam-customer">Customer</label>
            <select id="fam-customer" value={activeCustomerId} onChange={e => applyCustomerFilter(e.target.value)}>
              <option value="">None (show all)</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c._depth > 0 ? '— ' : ''}{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <button type="button" className="dd20-filter-btn" onClick={() => setShowFacilityFilter(true)}>
          Custom filter{selectedFacilityIds.size > 0 ? ` (${selectedFacilityIds.size})` : ''}
        </button>
      </div>

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && hierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={hierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {error && <div className="dd20-error">{error}</div>}
      {loading && <div className="dd20-progress">Loading facilities…</div>}

      {!loading && (
        <>
          <div className="dd20-section-header">
            <div>
              <h3>Facilities with mailing address</h3>
              <p className="dd20-view-hint">
                {visible.length.toLocaleString()} facilities
                {visible.length !== rows.length ? ` of ${rows.length.toLocaleString()}` : ''}
              </p>
            </div>
            <div className="dd20-section-actions">
              <button
                type="button"
                className="dd20-export-btn"
                disabled={!visible.length}
                title="Download the filtered facility list as Excel"
                onClick={() => {
                  const headers = [
                    'Facility name',
                    'VitalEngine facility ID',
                    'Address',
                    'City',
                    'State',
                    'State (raw)',
                    'Postal code',
                  ];
                  const objects = visible.map(r => ({
                    'Facility name': r.name || '',
                    'VitalEngine facility ID': r.veId || '',
                    Address: r.street || '',
                    City: r.city || '',
                    State: r.state || '',
                    'State (raw)': r.stateRaw || '',
                    'Postal code': r.postal || '',
                  }));
                  downloadObjectsAsXlsx(objects, {
                    fileStem: 'facilities_mailing_address',
                    sheetName: 'Mailing Address',
                    headers,
                  });
                }}
              >
                Export Excel
              </button>
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="dd-empty">No facilities match this state or scope.</div>
          ) : (
            <div className="dd-table-scroll">
              <table className="dd-heatmap fam-table">
                <thead>
                  <tr>
                    <th>Facility name</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Postal code</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(r => (
                    <tr key={r.id || r.veId || r.name}>
                      <td title={r.name}>{r.name}</td>
                      <td title={r.street}>{r.street || '—'}</td>
                      <td>{r.city || '—'}</td>
                      <td title={r.stateRaw && r.stateRaw !== r.state ? r.stateRaw : r.state}>{r.state || '—'}</td>
                      <td>{r.postal || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <details className="dd20-gaps">
        <summary>How this maps to the Tableau sheet</summary>
        <ul>
          <li>Tableau lists VitalEngine mailing and physical addresses as separate columns and filters on mailing state.</li>
          <li>VitalData stores one billing address from Salesforce. That is the address, city, state, and postal code shown here.</li>
        </ul>
      </details>

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={hierarchy}
          availableFacilityIds={dataFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => {
            setSelectedFacilityIds(ids);
            setActiveCustomerId('');
            setActiveFilterId('');
            clearGlobalScope();
          }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </div>
  );
}

export default FacilitiesMailing;
